// ResearchConsentStep.tsx — the research opt-in body, shown identically in two
// places so informed consent is collected the same way every time:
//   1. Onboarding (ConsentGate step 2), the first invitation.
//   2. Settings, whenever someone turns research back ON — we re-present this same
//      page and only record consent if they confirm here. (UK GDPR: research/
//      wellbeing data needs explicit, demonstrable, informed consent — a bare
//      toggle is not enough.)
// It renders only the content (badge → decision buttons); the caller supplies the
// surrounding frame (full onboarding frame, or the Settings re-consent sheet).
import React from 'react';
import { Display, Body, Btn } from '@/components/ui';
import { Icon, type IconName } from '@/components/icons';
import { RESEARCH_NOTICE_URL, RESEARCH_POINTS } from '@/config/consent';
import { ONBOARDING_2026 } from '@/config/features';
import { openExternal } from '@/services/openExternal';

const openUrl = (url: string) => openExternal(url);

const inlineLink: React.CSSProperties = {
  background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit',
  color: 'var(--amber-deep)', fontWeight: 600, textDecoration: 'underline',
};

function Badge({ name }: { name: IconName }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 40, height: 40, borderRadius: 999, marginBottom: 10,
        background: 'rgba(200,134,26,0.13)', color: 'var(--amber-deep)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Icon name={name} size={21} />
    </div>
  );
}

export function ResearchConsentStep({
  onDecide,
  saving = false,
}: {
  /** granted = "Yes, I consent"; declined = "Not just now". */
  onDecide: (granted: boolean) => void;
  saving?: boolean;
}) {
  const [pointsOpen, setPointsOpen] = React.useState(false);
  return (
    <div style={{ paddingTop: 4 }}>
      <Badge name="leaf" />
      <Display text="Take part in our research?" size={26} weight={400} lh={1.18} style={{ marginBottom: 10 }} />
      <Body size={15.5} style={{ lineHeight: 1.58, color: 'var(--ink-soft)', marginBottom: 16, maxWidth: '34ch' }}>
        ReNurture is being studied to find out whether it genuinely helps people recover. You can choose to let your check-ins, and the questions you ask the in-app guide, be part of that research.{' '}
        {ONBOARDING_2026 && (
          <>
            <strong style={{ color: 'var(--ink)', fontWeight: 700 }}>
              Everything used for research is anonymised
            </strong>
            : your name, your email and anything else that could identify you are removed before it is ever looked at.{' '}
          </>
        )}
        It’s completely voluntary, and it never changes anything about your access.
      </Body>

      {/* Neutral reassurance — no inducement. Your own progress view is available
          to everyone regardless of consent; taking part only adds an anonymised
          copy to the research. */}
      <div
        style={{
          display: 'flex', gap: 12, alignItems: 'flex-start',
          background: 'rgba(200,134,26,0.10)', border: '1px solid rgba(200,134,26,0.22)',
          borderRadius: 16, padding: '15px 16px', marginBottom: 22,
        }}
      >
        <span aria-hidden="true" style={{ flex: '0 0 auto', color: 'var(--amber-deep)', marginTop: 1 }}>
          <Icon name="sparkle" size={20} />
        </span>
        <Body size={14} style={{ lineHeight: 1.55 }}>
          Whatever you decide, your check-ins stay yours and you’ll always see your own progress. Taking part just means an{' '}
          {ONBOARDING_2026
            ? <strong style={{ color: 'var(--ink)', fontWeight: 700 }}>anonymised copy</strong>
            : 'anonymised copy'}{' '}
          also helps the research.
        </Body>
      </div>

      {/* The full consent terms. Eight points is a long read at the exact moment
          someone is being asked to decide, so ONBOARDING_2026 folds them into one
          expandable box: nothing is removed or softened, and the control names what
          is inside, but the page no longer opens as a wall of small print. */}
      {ONBOARDING_2026 ? (
        <div
          style={{
            background: 'var(--card-top)', border: '1px solid var(--hair)', borderRadius: 16,
            marginBottom: 14, overflow: 'hidden',
          }}
        >
          <button
            type="button"
            onClick={() => setPointsOpen((o) => !o)}
            aria-expanded={pointsOpen}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              background: 'none', border: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left',
              padding: '15px 16px', color: 'var(--ink)',
            }}
          >
            <span>
              <Body size={13.5} style={{ fontWeight: 700, lineHeight: 1.4, color: 'var(--ink)' }}>
                If you take part, here’s what you’re agreeing to
              </Body>
              <Body size={12.5} style={{ color: 'var(--ink-soft)', marginTop: 3 }}>
                {pointsOpen ? 'Tap to close' : `${RESEARCH_POINTS.length} points. Tap to read them.`}
              </Body>
            </span>
            <span
              aria-hidden="true"
              style={{
                flex: '0 0 auto', color: 'var(--amber-deep)', display: 'flex',
                transform: pointsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s',
              }}
            >
              <Icon name="chevDown" size={18} />
            </span>
          </button>
          {pointsOpen && (
            <div style={{ borderTop: '1px solid var(--hair)', padding: '16px 16px 6px' }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {RESEARCH_POINTS.map((p, i) => (
                  <li key={i} style={{ display: 'flex', gap: 11, marginBottom: 14 }}>
                    <span aria-hidden="true" style={{ flex: '0 0 auto', marginTop: 2, color: 'var(--amber-deep)' }}>
                      <Icon name="check" size={15} />
                    </span>
                    <Body size={13.5} style={{ lineHeight: 1.55 }}>{p}</Body>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <>
          <Body size={13} style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-soft)', marginBottom: 10 }}>
            If you take part, here’s what you’re agreeing to
          </Body>
          <div
            style={{
              background: 'var(--card-top)', border: '1px solid var(--hair)', borderRadius: 16,
              padding: '16px 16px 6px', marginBottom: 14,
            }}
          >
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {RESEARCH_POINTS.map((p, i) => (
                <li key={i} style={{ display: 'flex', gap: 11, marginBottom: 14 }}>
                  <span aria-hidden="true" style={{ flex: '0 0 auto', marginTop: 2, color: 'var(--amber-deep)' }}>
                    <Icon name="check" size={15} />
                  </span>
                  <Body size={13.5} style={{ lineHeight: 1.55 }}>{p}</Body>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
      <Body size={13} style={{ lineHeight: 1.55, marginBottom: 24 }}>
        The full detail, including your rights, is in our{' '}
        <button type="button" style={inlineLink} onClick={() => openUrl(RESEARCH_NOTICE_URL)}>Research Privacy Notice</button>.
      </Body>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Btn full size="lg" onClick={() => onDecide(true)} disabled={saving}>Yes, I consent</Btn>
        <Btn full size="lg" variant="ghost" onClick={() => onDecide(false)} disabled={saving}>Not just now</Btn>
      </div>
    </div>
  );
}
